/**
 * @fileoverview
 * Stepper Component - Multi-step progress indicator
 * Migrated to new architecture with OnPush change detection
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, inject, computed } from '@angular/core';

import { Router } from '@angular/router';

export interface Step {
  id: string;
  title: string;
  description?: string;
  completed?: boolean;
  active?: boolean;
}

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.css',
})
export class StepperComponent {
  private router = inject(Router);

  @Input() steps: Step[] = [];
  @Input() currentStepIndex: number = 0;
  @Input() showNavigation: boolean = true;
  @Input() allowBackNavigation: boolean = true;
  @Input() allowForwardNavigation: boolean = true;
  @Input() nextButtonText: string = 'Next';
  @Input() nextButtonDisabled: boolean = false;
  @Input() backButtonDisabled: boolean = false;
  @Input() baseRoute: string = '';
  /** When set, these query params are preserved when navigating between steps. */
  @Input() queryParams: Record<string, string> | null = null;

  @Output() stepChange = new EventEmitter<number>();
  @Output() nextClick = new EventEmitter<void>();

  currentStep = computed(() => {
    return this.steps[this.currentStepIndex] || null;
  });

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get progressPercentage(): number {
    if (this.steps.length === 0) return 0;
    return ((this.currentStepIndex + 1) / this.steps.length) * 100;
  }

  onNext(): void {
    if (this.allowForwardNavigation && !this.nextButtonDisabled) {
      this.nextClick.emit();
    }
  }

  goToStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.currentStepIndex = index;
      this.stepChange.emit(this.currentStepIndex);

      if (this.baseRoute && this.steps[index]) {
        this.router.navigate([this.baseRoute, this.steps[index].id], {
          queryParams: this.queryParams ?? undefined,
        });
      }
    }
  }

  getStepStatus(step: Step, index: number): string {
    if (index < this.currentStepIndex) {
      return 'completed';
    } else if (index === this.currentStepIndex) {
      return 'active';
    } else {
      return 'pending';
    }
  }
}

